import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Pencil, Trash2, FileText, Loader2, Eye, Copy, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const TIPOS = [
  { v: "locacao", l: "Locação" },
  { v: "venda", l: "Venda" },
  { v: "servico", l: "Serviço" },
  { v: "outros", l: "Outros" },
];

const EMPTY = {
  nome: "",
  tipo_contrato: "locacao",
  conteudo_html: "<h2>CONTRATO</h2>\n<p>Edite aqui...</p>",
  observacoes: "",
  ativo: true,
  padrao: false,
};

export default function ConfiguracoesModelos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [variaveis, setVariaveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);

  const canEdit = ["diretor", "admin_templates"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: modelos }, { data: vars }] = await Promise.all([
        api.get("/configuracoes/modelos-contrato"),
        api.get("/configuracoes/modelos-contrato/variaveis-disponiveis"),
      ]);
      setItems(modelos || []);
      setVariaveis(vars?.items || []);
    } catch {
      toast.error("Falha ao carregar modelos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const abrirNovo = () => {
    setEditing(null);
    setForm(EMPTY);
    setPreviewHtml("");
    setOpen(true);
  };

  const abrirEdicao = (m) => {
    setEditing(m);
    setForm({
      nome: m.nome,
      tipo_contrato: m.tipo_contrato,
      conteudo_html: m.conteudo_html,
      observacoes: m.observacoes || "",
      ativo: !!m.ativo,
      padrao: !!m.padrao,
    });
    setPreviewHtml("");
    setOpen(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { toast.error("Nome obrigatório"); return; }
    if (!form.conteudo_html.trim()) { toast.error("Conteúdo HTML obrigatório"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/configuracoes/modelos-contrato/${editing.id}`, form);
        toast.success("Modelo atualizado");
      } else {
        await api.post("/configuracoes/modelos-contrato", form);
        toast.success("Modelo criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remover = async (m) => {
    if (!window.confirm(`Remover modelo "${m.nome}"?`)) return;
    try {
      await api.delete(`/configuracoes/modelos-contrato/${m.id}`);
      toast.success("Removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const fazerPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(
        `/configuracoes/modelos-contrato/${editing?.id || "preview"}/preview`,
        { conteudo_html: form.conteudo_html, contexto: {} },
      );
      setPreviewHtml(data.html || "");
      toast.success("Preview renderizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no preview (erro de sintaxe?)");
    } finally {
      setPreviewing(false);
    }
  };

  const copiarVariavel = async (key) => {
    const txt = `{{${key}}}`;
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`Copiado: ${txt}`);
    } catch { toast.info(txt); }
  };

  return (
    <div className="space-y-5" data-testid="configuracoes-modelos-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <FileText size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Modelos de Contrato
            </h1>
            <p className="text-sm text-muted-foreground">
              Modelos HTML editáveis usados para gerar o contrato no MESMO PDF da proposta.
            </p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={abrirNovo} className="text-white" style={{ background: "#F97316" }} data-testid="novo-modelo-btn">
            <Plus size={14} className="mr-2" /> Novo modelo
          </Button>
        )}
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-center">Padrão</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead>Observações</TableHead>
              {canEdit && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="inline animate-spin" /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum modelo cadastrado.</TableCell></TableRow>
            ) : items.map((m) => (
              <TableRow key={m.id} data-testid={`modelo-${m.id}`}>
                <TableCell className="font-medium">{m.nome}</TableCell>
                <TableCell><Badge variant="outline">{TIPOS.find((t) => t.v === m.tipo_contrato)?.l || m.tipo_contrato}</Badge></TableCell>
                <TableCell className="text-center">{m.padrao ? "⭐" : "—"}</TableCell>
                <TableCell className="text-center">{m.ativo ? <span className="text-emerald-600">✓</span> : <span className="text-zinc-400">—</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate" title={m.observacoes}>{m.observacoes || "—"}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => abrirEdicao(m)} data-testid={`editar-modelo-${m.id}`}><Pencil size={14} /></Button>
                    {!m.padrao && <Button size="sm" variant="ghost" onClick={() => remover(m)} data-testid={`remover-modelo-${m.id}`}><Trash2 size={14} className="text-red-600" /></Button>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar modelo" : "Novo modelo de contrato"}</DialogTitle>
            <DialogDescription>HTML editável com variáveis Jinja2 — usado para gerar o contrato no mesmo PDF da proposta.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome *</Label>
                  <Input data-testid="modelo-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Locação Premium 24 meses" />
                </div>
                <div>
                  <Label>Tipo de contrato</Label>
                  <select data-testid="modelo-tipo" value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })} className="w-full border rounded h-9 px-2 text-sm bg-background">
                    {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Observações</Label>
                <Input data-testid="modelo-obs" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Notas internas..." />
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch data-testid="modelo-ativo" checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
                  <Label>Ativo</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch data-testid="modelo-padrao" checked={form.padrao} onCheckedChange={(v) => setForm({ ...form, padrao: v })} />
                  <Label>Padrão do tipo</Label>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Conteúdo HTML do contrato</Label>
                  <Button size="sm" variant="outline" onClick={fazerPreview} disabled={previewing} data-testid="modelo-preview-btn">
                    {previewing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Eye size={12} className="mr-1" />}
                    Preview
                  </Button>
                </div>
                <Textarea
                  data-testid="modelo-conteudo"
                  rows={18}
                  value={form.conteudo_html}
                  onChange={(e) => setForm({ ...form, conteudo_html: e.target.value })}
                  className="font-mono text-xs"
                  placeholder='<h2>CONTRATO Nº {{numero_proposta}}</h2>...'
                />
                <div className="text-[11px] text-muted-foreground mt-1 flex items-start gap-1">
                  <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
                  Use sintaxe Jinja2: <code className="bg-slate-100 px-1 rounded">{`{{nome_cliente}}`}</code>, loops com <code className="bg-slate-100 px-1 rounded">{`{% for eq in equipamentos %}...{% endfor %}`}</code>.
                </div>
              </div>
              {previewHtml && (
                <Card className="p-4 max-h-96 overflow-y-auto bg-white border-blue-300" data-testid="modelo-preview-content">
                  <div className="text-xs text-blue-700 font-semibold mb-2 border-b pb-1">PREVIEW renderizado com dados de exemplo</div>
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} className="prose prose-sm max-w-none" />
                </Card>
              )}
            </div>
            <div className="col-span-4">
              <Card className="p-3 bg-slate-50 sticky top-0">
                <Label className="text-sm font-bold">Variáveis disponíveis</Label>
                <p className="text-[10px] text-muted-foreground mb-2">Clique para copiar</p>
                <div className="space-y-1 max-h-[440px] overflow-y-auto">
                  {variaveis.map((v) => (
                    <button
                      key={v.key}
                      onClick={() => copiarVariavel(v.key)}
                      data-testid={`var-${v.key}`}
                      className="w-full text-left p-1.5 rounded hover:bg-white border border-transparent hover:border-blue-200 transition"
                    >
                      <div className="flex items-center gap-1">
                        <Copy size={9} className="text-slate-400" />
                        <code className="text-[10.5px] font-mono text-blue-700">{`{{${v.key}}}`}</code>
                      </div>
                      <div className="text-[10px] text-muted-foreground ml-3.5">{v.label}</div>
                    </button>
                  ))}
                </div>
              </Card>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={salvar} disabled={saving} className="text-white" style={{ background: "#1B3A6B" }} data-testid="modelo-salvar">
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              {editing ? "Salvar alterações" : "Criar modelo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
